import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, AlertTriangle, FileDown } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";

export const TemplateCallout = ({ testId, children }) => (
  <aside data-testid={testId} className="not-prose my-5 rounded-xl border-l-4 border-[var(--amber)] bg-[var(--amber)]/10 p-4 text-[var(--amber)] text-[13px] leading-relaxed font-mono">
    <p className="font-semibold uppercase tracking-wider text-[11px] mb-1 flex items-center gap-2">
      <AlertTriangle size={13} /> Placeholder — complete before public launch
    </p>
    <div className="[&_p]:text-amber-100/90 [&_p]:text-[13px] [&_p]:mb-0">{children}</div>
  </aside>
);

export const LegalLayout = ({ title, eyebrow, updated, toc, testId, backTestId, children }) => {
  useEffect(() => {
    document.title = `${title} — BeaconShield`;
  }, [title]);

  const jump = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -96 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.main
      data-testid={testId}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-transparent"
    >
      <Nav />
      <header className="legal-header relative bg-transparent border-b border-white/10">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-32 pb-14 sm:pt-40 sm:pb-20">
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/" data-testid={backTestId} className="print:hidden inline-flex items-center gap-2 text-sm font-medium text-[var(--clinical)] hover:text-white transition-colors">
              <ArrowLeft size={16} /> Back to home
            </Link>
            <span className="h-4 w-px bg-white/15 print:hidden" aria-hidden="true" />
            <button
              type="button"
              onClick={() => window.print()}
              data-testid={`${testId}-download-pdf`}
              className="print:hidden inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-sm font-medium text-slate-200 hover:border-[var(--clinical)]/50 hover:text-[var(--clinical)] transition-colors"
            >
              <FileDown size={15} /> Download PDF
            </button>
          </div>
          <p className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--clinical)] flex items-center gap-3">
            <span className="h-px w-8 bg-[var(--clinical)]" /> {eyebrow}
          </p>
          <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] text-white leading-[1.02]">{title}</h1>
          <p className="mt-5 font-mono text-xs text-[#b7c2d4]">Last updated: {updated}</p>
          <p className="hidden print:block mt-2 font-mono text-xs text-[#b7c2d4]">BeaconShield, Inc. · https://beacon-shield.com{window.location.pathname}</p>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <nav aria-label="Contents" className="hidden lg:block lg:col-span-3 print:hidden">
          <div className="sticky top-28">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Contents</p>
            <ol className="mt-4 space-y-2 border-l border-white/10">
              {toc.map((t, i) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} onClick={jump(t.id)} className="block -ml-px border-l border-transparent pl-4 text-sm text-[#b7c2d4] hover:text-white hover:border-[var(--clinical)] transition-colors">
                    <span className="font-mono text-[11px] text-slate-500 mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {t.label}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </nav>
        <article className="legal-prose lg:col-span-8 lg:col-start-5 max-w-3xl">{children}</article>
      </div>
      <Footer />
    </motion.main>
  );
};
